import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  BuildingOfficeIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  DocumentTextIcon,
  CalculatorIcon,
  UserGroupIcon,
  BellIcon,
  InformationCircleIcon,
  PaintBrushIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  GlobeAltIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

const storefrontNavigation = [
  {
    name: 'My Store',
    items: [
      { name: 'Contact Information', href: 'contact', icon: PhoneIcon },
      // { name: 'Sales Channels', href: 'sales-channels', icon: GlobeAltIcon },
      { name: 'Opening Hours', href: 'hours', icon: ClockIcon },
      { name: 'Emails', href: 'emails', icon: EnvelopeIcon },
    ]
  },
  {
    name: 'Settings',
    items: [
      // { name: 'Business Details', href: 'business-details', icon: BuildingOfficeIcon },
      { name: 'General', href: 'general', icon: Cog6ToothIcon },
      { name: 'Item Reservations', href: 'item-reservations', icon: ClipboardDocumentListIcon },
      // { name: 'Branding & Appearance', href: 'branding', icon: PaintBrushIcon },
      { name: 'Payments', href: 'payments', icon: CreditCardIcon },
      { name: 'Terms & Policies', href: 'terms', icon: DocumentTextIcon },
      { name: 'Taxes', href: 'taxes', icon: CalculatorIcon },
      { name: 'Users & Permissions', href: 'permissions', icon: UserGroupIcon },
      // { name: 'Notification Preferences', href: 'notifications', icon: BellIcon },
      { name: 'Account', href: 'account', icon: InformationCircleIcon },
    ]
  }
];

const Storefront = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAdmin, isManager } = useAuth();

  // Filter navigation items based on user permissions
  const getFilteredNavigation = () => {
    return storefrontNavigation.map(section => ({
      ...section,
      items: section.items.filter(item => {
        // Hide Users & Permissions from regular employees
        if (item.name === 'Users & Permissions') {
          return isAdmin || isManager;
        }
        return true;
      })
    }));
  };

  const handleNavClick = (href) => {
    console.log('Storefront navigation - navigating to:', href, 'from:', location.pathname);
    navigate(href);
    setSidebarOpen(false);
  };

  const isActive = (href) => location.pathname === `/storefront/${href}`;

  // If we're on the main storefront page, show the overview
  const isMainPage = location.pathname === '/storefront';

  return (
    <div className="flex h-full">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col">
        <div className="flex flex-col flex-grow border-r border-gray-200 pt-5 bg-white overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-4">
            <BuildingOfficeIcon className="h-8 w-8 text-indigo-600" />
            <div className="ml-3">
              <h1 className="text-lg font-semibold text-gray-900">Settings</h1>
              <p className="text-sm text-gray-500">Management</p>
            </div>
          </div>
          
          <div className="mt-8 flex-grow flex flex-col">
            <nav className="flex-1 px-2 space-y-8">
              {getFilteredNavigation().map((section) => (
                <div key={section.name}>
                  <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {section.name}
                  </h3>
                  <div className="mt-2 space-y-1">
                    {section.items.map((item) => (
                      <button
                        key={item.name}
                        onClick={() => handleNavClick(item.href)}
                        className={`${
                          isActive(item.href)
                            ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-600'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        } group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left transition-colors duration-150`}
                      >
                        <item.icon
                          className={`${
                            isActive(item.href) 
                              ? 'text-indigo-500' 
                              : 'text-gray-400 group-hover:text-gray-500'
                          } mr-3 h-5 w-5 flex-shrink-0`}
                        />
                        {item.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <BuildingOfficeIcon className="h-8 w-8 text-indigo-600" />
              <div className="ml-3">
                <h1 className="text-lg font-semibold text-gray-900">Settings</h1>
                <p className="text-sm text-gray-500">Management</p>
              </div>
            </div>
            <nav className="mt-5 px-2 space-y-1">
              {getFilteredNavigation().map((section) => (
                <div key={section.name} className="mb-6">
                  <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {section.name}
                  </h3>
                  {section.items.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => handleNavClick(item.href)}
                      className="text-gray-600 hover:bg-gray-50 hover:text-gray-900 group flex items-center px-2 py-2 text-base font-medium rounded-md w-full text-left"
                    >
                      <item.icon className="text-gray-400 group-hover:text-gray-500 mr-4 h-6 w-6" />
                      {item.name}
                    </button>
                  ))}
                </div>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 md:pl-0">
        {/* Mobile header */}
        <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-100">
          <button
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Page content */}
        <main className="flex-1 relative z-0 focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {isMainPage ? (
                <div>
                  <div className="md:flex md:items-center md:justify-between">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                        Settings
                      </h2>
                      <p className="mt-1 text-gray-600">
                        Manage your store settings, branding, and configuration
                      </p>
                    </div>
                  </div>

                  {/* Overview cards */}
                  <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {getFilteredNavigation().map((section) => (
                      <div key={section.name} className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <InformationCircleIcon className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                              <dl>
                                <dt className="text-sm font-medium text-gray-500 truncate">
                                  {section.name}
                                </dt>
                                <dd className="text-lg font-medium text-gray-900">
                                  {section.items.length} settings
                                </dd>
                              </dl>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-50 px-5 py-3">
                          <div className="space-y-1">
                            {section.items.slice(0, 3).map((item) => (
                              <button
                                key={item.name}
                                onClick={() => handleNavClick(item.href)}
                                className="text-sm text-indigo-600 hover:text-indigo-900 block"
                              >
                                {item.name}
                              </button>
                            ))}
                            {section.items.length > 3 && (
                              <p className="text-sm text-gray-500">
                                +{section.items.length - 3} more
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Outlet />
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Storefront;